import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  FormInstance,
  Input,
  Row,
  Spin
} from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { FormItemProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceCreateAnonymousPersonEndpoint,
  usePersonsServiceCreatePersonEndpoint,
  usePersonsServiceGetPersonListByDataEndpoint,
  usePersonsServiceGetPersonListByDataEndpointKey,
  usePersonsServiceGetPersonListEndpointKey,
  useTreatmentServiceCreatePotentialContactEndpoint,
  useTreatmentServiceGetListPotentialContactEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CountrySelect,
  GenderSelect,
  message,
  SaveConfirm
} from 'src/components'
import {
  dayMonthYearFormatWithDot,
  disabledFutureDate,
  formatDate,
  legalAgePickerValue,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from '../TutorForm/styles'

import { AddPersonAlert } from './AddPersonAlert'
import { SearchPersonTable } from './SearchPersonTable'

type Props = {
  isAddPersonAlertOpen?: boolean
  onCancel?: () => void
  onCloseModal?: () => void
  form?: FormInstance
}

const formRules: FormItemProps['rules'] = [
  {
    min: 3
  }
]

export const PersonalDataSearch = ({
  isAddPersonAlertOpen,
  onCancel,
  onCloseModal,
  form: formConsultation
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const { styles } = useStyles()

  const { id } = useParams()

  const params = Form.useWatch([], form)
  const shouldSearchInMConnect = Form.useWatch('shouldSearchInMConnect', form)
  const isAnonymous = Form.useWatch('anonymous', form)

  const queryClient = useQueryClient()

  const filteredParams = Object.fromEntries(
    Object.entries(params ?? {}).filter(([_, value]) => value !== '')
  )

  const birthDate =
    filteredParams.birthDate && dayjs.isDayjs(filteredParams?.birthDate)
      ? filteredParams.birthDate.format(yearMonthDaySlash)
      : undefined

  const { data, refetch, isLoading, isFetched } =
    usePersonsServiceGetPersonListByDataEndpoint(
      {
        shouldSearchInMConnect: false,
        ...filteredParams,
        birthDate
      },
      [],
      { enabled: false }
    )

  const allValues = Form.useWatch([], form) ?? {}
  const {
    idnp: _idnp,
    shouldSearchInMConnect: _shouldSearchInMConnect,
    citizenship: _citizenship,
    ...otherFields
  } = allValues

  const hasOtherFields = Object.values(otherFields).some((value) => !!value)

  const hasIDNP = Form.useWatch('idnp', form)

  const { mutate } = usePersonsServiceCreatePersonEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonListEndpointKey]
      })

      onAddContacts(String(data?.id))
    }
  })

  const { mutate: addAnonymousPerson } =
    usePersonsServiceCreateAnonymousPersonEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: (data) => {
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetPersonListEndpointKey]
        })

        onAddContacts(String(data?.id))
      }
    })

  const { mutate: addContacts } =
    useTreatmentServiceCreatePotentialContactEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetListPotentialContactEndpointKey]
        })

        onCancel?.()
        onCloseModal?.()
      },
      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onAddContacts = async (personId: string) => {
    const values = await form.validateFields()

    if (formConsultation) {
      const contacts = formConsultation.getFieldValue('contactsList') ?? []

      const newContact = {
        personId: personId,
        contactDate: values.contactDate
          ? formatDate(values.contactDate, yearMonthDaySlash)
          : undefined,
        comments: values.comments
      }

      formConsultation.setFieldValue('contactsList', [...contacts, newContact])
      onCancel?.()
      onCloseModal?.()
      return
    }

    const requestBody = {
      contacts: [
        {
          personId: personId,
          contactDate:
            values.contactDate &&
            formatDate(values.contactDate, yearMonthDaySlash),
          comments: values.comments
        }
      ]
    }

    addContacts({ consultationId: String(id), requestBody })
  }

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (isAnonymous && isAddPersonAlertOpen) {
      const requestBody = {
        firstName: values.firstName,
        lastName: values.lastName,
        birthDate: formatDate(values.birthDate, yearMonthDaySlash),
        gender: values.gender
      }

      return addAnonymousPerson({
        requestBody
      })
    }

    if (isAddPersonAlertOpen) {
      mutate({
        requestBody: {
          firstName: values.firstName,
          lastName: values.lastName,
          idnp: values.idnp,
          birthDate: formatDate(values.birthDate, yearMonthDaySlash),
          citizenship: values.citizenship,
          gender: values.gender
        }
      })
    } else {
      const hasAtLeastOneValue = Object.values(values).some((value) => !!value)

      if (!hasAtLeastOneValue) {
        message.error(t('errors.fill_at_least_one_field'))
        return
      }

      refetch()
    }
  }

  const handleCancel = () => {
    queryClient.removeQueries({
      queryKey: [usePersonsServiceGetPersonListByDataEndpointKey, params]
    })
    form.resetFields()
    onCancel?.()
  }

  useEffect(() => {
    if (isAddPersonAlertOpen) {
      form.resetFields()
    }
  }, [isAddPersonAlertOpen])

  const renderContent = () => {
    if (isAddPersonAlertOpen) return null
    if (isLoading) return <Spin />
    if (isFetched && !data?.data?.length)
      return (
        <AddPersonAlert
          form={form}
          onCancel={onCancel}
          formConsultation={formConsultation}
        />
      )
    if (data?.data)
      return (
        <SearchPersonTable
          isLoading={isLoading}
          onCancel={handleCancel}
          form={formConsultation}
          data={data?.data}
        />
      )
  }

  return (
    <Form requiredMark={false} layout="vertical" form={form}>
      <Flex vertical gap={24}>
        <Flex vertical gap={24}>
          <Row gutter={[24, 24]}>
            {isAddPersonAlertOpen && (
              <Col xs={24} lg={24}>
                <Form.Item name="anonymous" valuePropName="checked">
                  <Checkbox>
                    {t('epidemiology.patient_status.anonymous')}
                  </Checkbox>
                </Form.Item>
              </Col>
            )}

            {!isAnonymous && (
              <Col xs={24} lg={12}>
                <Form.Item
                  name="citizenship"
                  label={t('common.citizenship')}
                  rules={isAddPersonAlertOpen ? requiredRules : []}
                  initialValue="MD"
                >
                  <CountrySelect
                    allowClear
                    placeholder={t('common.citizenship')}
                    disabled={
                      !isAddPersonAlertOpen &&
                      (hasIDNP || shouldSearchInMConnect)
                    }
                  />
                </Form.Item>
              </Col>
            )}

            {!isAnonymous && (
              <Col xs={24} lg={12}>
                <Form.Item
                  name="idnp"
                  label={t('form.idnp')}
                  rules={[
                    {
                      min: 13,
                      max: 13,
                      required: !!shouldSearchInMConnect
                    }
                  ]}
                >
                  <Input
                    placeholder={t('form.idnp')}
                    className="full-width"
                    disabled={!isAddPersonAlertOpen && hasOtherFields}
                  />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} lg={12}>
              <Form.Item
                name="lastName"
                label={t('form.last_name')}
                rules={[
                  ...(isAddPersonAlertOpen ? (requiredRules ?? []) : []),
                  ...(formRules ?? [])
                ]}
              >
                <Input
                  placeholder={t('form.last_name')}
                  disabled={
                    !isAddPersonAlertOpen && (hasIDNP || shouldSearchInMConnect)
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="firstName"
                label={t('form.first_name')}
                rules={[
                  ...(isAddPersonAlertOpen ? (requiredRules ?? []) : []),
                  ...(formRules ?? [])
                ]}
              >
                <Input
                  placeholder={t('form.first_name')}
                  disabled={
                    !isAddPersonAlertOpen && (hasIDNP || shouldSearchInMConnect)
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="birthDate"
                label={t('form.birth_date')}
                rules={[...(isAddPersonAlertOpen ? (requiredRules ?? []) : [])]}
              >
                <DatePicker
                  className="full-width"
                  placeholder={t('form.birth_date')}
                  defaultPickerValue={legalAgePickerValue}
                  format={dayMonthYearFormatWithDot}
                  disabled={
                    !isAddPersonAlertOpen && (hasIDNP || shouldSearchInMConnect)
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="gender"
                label={t('form.gender')}
                rules={[...(isAddPersonAlertOpen ? (requiredRules ?? []) : [])]}
              >
                <GenderSelect
                  disabled={
                    !isAddPersonAlertOpen && (hasIDNP || shouldSearchInMConnect)
                  }
                />
              </Form.Item>
            </Col>

            {isAddPersonAlertOpen && (
              <>
                <Col xs={24} lg={24}>
                  <Form.Item name="contactDate" label={t('form.contact_date')}>
                    <DatePicker
                      className="full-width"
                      placeholder={t('form.contact_date')}
                      disabledDate={disabledFutureDate}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={24} className="full-width">
                  <Form.Item name="comments" label={t('common.comments')}>
                    <TextArea
                      className={styles.textarea}
                      placeholder={t('common.comments')}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          {!shouldSearchInMConnect && (
            <Flex justify="center">
              {isAddPersonAlertOpen ? (
                <SaveConfirm title={`${t('common.add')}?`} onConfirm={onSubmit}>
                  <Button type="primary">{t('common.add')}</Button>
                </SaveConfirm>
              ) : (
                <Button type="primary" onClick={onSubmit}>
                  {t('persons.search_persons')}
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        {renderContent()}
      </Flex>
    </Form>
  )
}
